import { AssetType } from './types/Asset';

export type AssetTypeSlug = 'pets' | 'vehicles' | 'properties' | 'other';

export type AssetFieldKind = 'text' | 'textarea' | 'number' | 'date' | 'checkbox' | 'select';

export interface AssetFieldOption {
  value: string;
  labelKey: string;
}

export interface AssetFieldDefinition {
  key: string;
  labelKey: string;
  kind: AssetFieldKind;
  options?: AssetFieldOption[];
  fullWidth?: boolean;
  list?: boolean;
}

export interface AssetTypeDefinition {
  type: AssetType;
  slug: AssetTypeSlug;
  labelKey: string;
  identifierLabelKey: string;
  fields: AssetFieldDefinition[];
}

const genderOptions: AssetFieldOption[] = [
  { value: 'female', labelKey: 'genders.female' },
  { value: 'male', labelKey: 'genders.male' },
  { value: 'unknown', labelKey: 'genders.unknown' },
];

const petStatusOptions: AssetFieldOption[] = [
  { value: 'active', labelKey: 'petStatuses.active' },
  { value: 'deceased', labelKey: 'petStatuses.deceased' },
];

const fuelOptions: AssetFieldOption[] = [
  { value: 'petrol', labelKey: 'fuelTypes.petrol' },
  { value: 'diesel', labelKey: 'fuelTypes.diesel' },
  { value: 'electric', labelKey: 'fuelTypes.electric' },
  { value: 'hybrid', labelKey: 'fuelTypes.hybrid' },
];

const occupancyOptions: AssetFieldOption[] = [
  { value: 'ownerOccupied', labelKey: 'occupancy.ownerOccupied' },
  { value: 'rented', labelKey: 'occupancy.rented' },
  { value: 'vacant', labelKey: 'occupancy.vacant' },
];

export const ASSET_TYPE_MODULES: AssetTypeDefinition[] = [
  {
    type: 'Pet',
    slug: 'pets',
    labelKey: 'assetTypes.pet',
    identifierLabelKey: 'identifiers.microchip',
    fields: [
      { key: 'species', labelKey: 'species', kind: 'text', list: true },
      { key: 'breed', labelKey: 'breed', kind: 'text', list: true },
      { key: 'color', labelKey: 'color', kind: 'text' },
      { key: 'gender', labelKey: 'gender', kind: 'select', options: genderOptions },
      { key: 'dateOfBirth', labelKey: 'dateOfBirth', kind: 'date' },
      { key: 'dateOfDeath', labelKey: 'dateOfDeath', kind: 'date' },
      { key: 'status', labelKey: 'status', kind: 'select', options: petStatusOptions },
      { key: 'passportNumber', labelKey: 'passportNumber', kind: 'text' },
      { key: 'lastVaccinationDate', labelKey: 'lastVaccinationDate', kind: 'date' },
      { key: 'acquiredHow', labelKey: 'acquiredHow', kind: 'text' },
      { key: 'acquiredDate', labelKey: 'acquiredDate', kind: 'date' },
      { key: 'isInsured', labelKey: 'insured', kind: 'checkbox', fullWidth: true },
      { key: 'insurerName', labelKey: 'insurerName', kind: 'text' },
      { key: 'insuranceNumber', labelKey: 'insuranceNumber', kind: 'text' },
    ],
  },
  {
    type: 'Vehicle',
    slug: 'vehicles',
    labelKey: 'assetTypes.vehicle',
    identifierLabelKey: 'identifiers.vin',
    fields: [
      { key: 'make', labelKey: 'make', kind: 'text', list: true },
      { key: 'model', labelKey: 'model', kind: 'text' },
      { key: 'year', labelKey: 'year', kind: 'number' },
      { key: 'color', labelKey: 'color', kind: 'text' },
      { key: 'licensePlate', labelKey: 'licensePlate', kind: 'text', list: true },
      { key: 'mileage', labelKey: 'mileage', kind: 'number' },
      { key: 'fuelType', labelKey: 'fuelType', kind: 'select', options: fuelOptions },
      { key: 'bodyType', labelKey: 'bodyType', kind: 'text' },
      { key: 'registrationExpiry', labelKey: 'registrationExpiry', kind: 'date' },
    ],
  },
  {
    type: 'Property',
    slug: 'properties',
    labelKey: 'assetTypes.property',
    identifierLabelKey: 'identifiers.cadastral',
    fields: [
      { key: 'address', labelKey: 'address', kind: 'text', fullWidth: true, list: true },
      { key: 'city', labelKey: 'city', kind: 'text', list: true },
      { key: 'postalCode', labelKey: 'postalCode', kind: 'text' },
      { key: 'areaSqm', labelKey: 'areaSqm', kind: 'number' },
      { key: 'rooms', labelKey: 'rooms', kind: 'number' },
      { key: 'floor', labelKey: 'floor', kind: 'number' },
      { key: 'buildingYear', labelKey: 'buildingYear', kind: 'number' },
      { key: 'occupancyStatus', labelKey: 'occupancyStatus', kind: 'select', options: occupancyOptions },
    ],
  },
  {
    type: 'Other',
    slug: 'other',
    labelKey: 'assetTypes.other',
    identifierLabelKey: 'identifiers.identifier',
    fields: [
      { key: 'category', labelKey: 'category', kind: 'text', list: true },
      { key: 'description', labelKey: 'description', kind: 'textarea', fullWidth: true },
    ],
  },
];

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getAssetTypeModule = (type: AssetType): AssetTypeDefinition =>
  ASSET_TYPE_MODULES.find(module => module.type === type) || ASSET_TYPE_MODULES[ASSET_TYPE_MODULES.length - 1];

export const getAssetTypeBySlug = (slug?: string): AssetTypeDefinition | undefined => {
  if (slug?.toLowerCase() === 'houses') {
    return ASSET_TYPE_MODULES.find(module => module.type === 'Property');
  }
  return ASSET_TYPE_MODULES.find(module => module.slug === slug);
};

export const isAssetTypeSlug = (value?: string): boolean => Boolean(getAssetTypeBySlug(value));

export const isAssetIdParam = (value?: string): boolean => Boolean(value && GUID_PATTERN.test(value));

export const assetTypePath = (type: AssetType, suffix = ''): string =>
  `/assets/${getAssetTypeModule(type).slug}${suffix}`;

export const assetsListPath = (type?: AssetType): string =>
  type ? `/assets?type=${getAssetTypeModule(type).slug}` : '/assets';

export const emptyDetailsFor = (type: AssetType): Record<string, unknown> => {
  const details: Record<string, unknown> = {};
  getAssetTypeModule(type).fields.forEach(field => {
    details[field.key] = field.kind === 'checkbox' ? false : '';
  });
  return details;
};

export const serializeDetails = (type: AssetType, values: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  getAssetTypeModule(type).fields.forEach(field => {
    const value = values[field.key];
    if (field.kind === 'checkbox') {
      result[field.key] = value === true;
      return;
    }
    if (value === '' || value === undefined || value === null) {
      result[field.key] = null;
      return;
    }
    result[field.key] = value;
  });
  return result;
};

export const detailsSearchText = (type: AssetType, details: Record<string, unknown>): string =>
  getAssetTypeModule(type).fields
    .map(field => String(details[field.key] ?? ''))
    .join(' ')
    .toLowerCase();

export const listFieldsFor = (type: AssetType): AssetFieldDefinition[] =>
  getAssetTypeModule(type).fields.filter(field => field.list);

